import { useEffect, useRef, useState } from 'react';
import { Spinner } from '@/components/glitch-ui/spinner';
import { SavedIndicator } from '@/components/glitch-ui/saved-indicator';
import { Button } from '@/components/ui/button';
import type { ButtonProps } from '@/components/ui/button';
import { cn } from '@/lib/utils';

// Once saving starts, hold the "Saving..." state for at least this long so a fast (sub-500ms) save
// shows a smooth animation instead of flashing the spinner in and straight back out (#5347 polish).
const MIN_SAVING_MS = 500;

interface SaveChangesButtonProps extends Omit<ButtonProps, 'children'> {
  /** True while the save request is in flight: shows the saving label and disables the button. */
  saving?: boolean;
  /**
   * True when saving pushes changes straight to an external platform (e.g. a LIVE campaign writing
   * to Google Ads). Renders green per the design rule (green is reserved for actions that push to
   * an external platform); otherwise the standard cobalt primary. (#5581)
   */
  pushesLive?: boolean;
  /**
   * True right after a successful save. Renders the "Saved ✓" confirmation IN PLACE of the button
   * (mutually exclusive, never beside it) — callers should not render a separate SavedIndicator.
   * `saving` takes precedence. Callers typically pass `saved={saveSuccess && !isDirty}`.
   */
  saved?: boolean;
  /** Idle label (default "Save Changes"). */
  label?: string;
  /** In-flight label (default "Saving..."). */
  savingLabel?: string;
  /** Saved-confirmation label (default "Saved"). */
  savedLabel?: string;
}

/**
 * Standard Save Changes button (#5581 / #5347). One place for the explicit-save affordance:
 * consistent cobalt primary, a green variant when the save pushes to an external platform, and a
 * built-in Save/Saving state that disables the button while a request is in flight.
 */
export function SaveChangesButton({
  saving = false,
  saved = false,
  pushesLive = false,
  label = 'Save Changes',
  savingLabel = 'Saving...',
  savedLabel = 'Saved',
  disabled,
  className,
  ...props
}: SaveChangesButtonProps) {
  // Debounce the saving state OFF (not on): show it immediately, but keep it for MIN_SAVING_MS so a
  // quick save doesn't flicker. Reading nothing but `saving` keeps the effect's deps honest.
  const [showSaving, setShowSaving] = useState(false);
  const startedAt = useRef(0);
  useEffect(() => {
    if (saving) {
      startedAt.current = Date.now();
      setShowSaving(true);
      return;
    }
    const remaining = Math.max(0, MIN_SAVING_MS - (Date.now() - startedAt.current));
    const timer = setTimeout(() => setShowSaving(false), remaining);
    return () => clearTimeout(timer);
  }, [saving]);

  // Once a save lands (and the min-duration saving animation has run out), the "Saved ✓"
  // confirmation replaces the button entirely — never alongside it. Deliberately NOT the button's
  // `className`: its chrome (shadow-lg, bg) would box the plain green text. Keep only the entrance
  // animation for continuity with the button leaving.
  if (saved && !showSaving) {
    return <SavedIndicator className="animate-bounce-in-right" label={savedLabel} />;
  }

  return (
    <Button
      type="button"
      disabled={disabled || showSaving}
      className={cn(
        'text-white',
        pushesLive ? 'bg-green-600 hover:bg-green-700' : 'bg-[#290BD9] hover:bg-[#2309C5]',
        className,
      )}
      {...props}
    >
      {showSaving ? (
        <>
          <Spinner size="sm" className="mr-2" />
          {savingLabel}
        </>
      ) : (
        label
      )}
    </Button>
  );
}
